import { Injectable, signal } from '@angular/core';

/** Which release home a user is looking at: the tester split or the manager flat list. */
export type ReleaseHomeView = 'mine' | 'everyone';

@Injectable({ providedIn: 'root' })
export class ReleaseViewStateService {
  // last selected plan id per release id
  private mapSig = signal<Record<number, number | null>>({});

  // last plan search term per release id, so toggling between the tester and
  // manager views of a release does not silently discard what the user typed.
  private searchSig = signal<Record<number, string>>({});

  // The view the user last picked from the toggle. Unlike the maps above this is
  // session-wide rather than per release, so a lead who switches to "My view" keeps
  // it while clicking between releases. Null means they have not chosen, and the
  // role decides (leads and admins default to the manager view, Bug 56800).
  // Deliberately in memory only: it resets on the next sign-in, which is when the
  // role default is meant to take over again.
  private viewOverrideSig = signal<ReleaseHomeView | null>(null);

  readonly viewOverride = this.viewOverrideSig.asReadonly();

  getSelectedPlanId(releaseId: number): number | null {
    const inMem = this.mapSig()[releaseId];
    if (inMem != null) return inMem;
    const key = this.key(releaseId);
    const raw = sessionStorage.getItem(key);
    return raw ? Number(raw) : null;
  }

  setSelectedPlanId(releaseId: number, planId: number | null): void {
    const next = { ...this.mapSig() };
    next[releaseId] = planId;
    this.mapSig.set(next);
    const key = this.key(releaseId);
    if (planId == null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, String(planId));
  }

  getSearch(releaseId: number): string {
    const inMem = this.searchSig()[releaseId];
    if (inMem != null) return inMem;
    return sessionStorage.getItem(this.searchKey(releaseId)) ?? '';
  }

  setSearch(releaseId: number, term: string): void {
    const next = { ...this.searchSig() };
    next[releaseId] = term;
    this.searchSig.set(next);
    const key = this.searchKey(releaseId);
    if (term === '') sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, term);
  }

  /** Record the view the user picked from the toggle for the rest of this session. */
  setViewOverride(view: ReleaseHomeView): void {
    this.viewOverrideSig.set(view);
  }

  private key(releaseId: number) {
    return `release:lastSelectedPlanId:${releaseId}`;
  }

  private searchKey(releaseId: number) {
    return `release:search:${releaseId}`;
  }
}
